import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { performLogout } from "../../utils/auth";
import { getAuthToken, ACCENT } from "../artistStudio/shared";
import { parentalApi } from "./parentalApi";
import { AskMoreTime } from "./Requests";

// Parental Control 13+ (sheet 1.2.1 to 1.2.3): runs for every signed-in account and does nothing unless a parent set time limits
// for a linked teen. For that teen it
//   - sends a heartbeat every 30 seconds while the page is in use; the SERVER adds the time up, so this page cannot change the count,
//   - shows a lock screen when the daily time is used up or it is outside the allowed hours, and pauses any audio or video,
//   - warns when 5 minutes or less are left.
// Any failure here is ignored: a broken check must never stop an adult, or a teen without limits, from using the site.
const BEAT_MS = 30 * 1000;
const WARN_SECONDS = 5 * 60;

const pauseMedia = () => {
  try { document.querySelectorAll("audio, video").forEach((m) => { if (!m.paused) m.pause(); }); } catch (e) { /* ignore */ }
};
export const minutesLeft = (seconds) => Math.max(1, Math.ceil(seconds / 60));

export function LockScreen({ status }) {
  const budget = status.reason === "budget";
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white px-6" role="alertdialog" aria-modal="true" aria-labelledby="lock-title" data-testid="usage-lock">
      <div className="max-w-md text-center">
        <h1 id="lock-title" className="text-2xl font-bold text-gray-900">{budget ? "That's all for today" : "Not available right now"}</h1>
        <p className="mt-3 text-gray-600">{status.message}</p>
        <p className="mt-2 text-sm text-gray-500">Your parent or guardian set these limits. Ask them if you need to change them.</p>
        {budget && status.limit_seconds !== null && <AskMoreTime />}
        <button type="button" onClick={() => performLogout()} className="mt-6 rounded-full px-6 py-2 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>Log out</button>
      </div>
    </div>
  );
}

export default function UsageGuard() {
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const tokenSeen = useRef(null);
  const timer = useRef(null);

  // (Re)start when somebody signs in or out.
  useEffect(() => {
    const token = getAuthToken() || null;
    if (token === tokenSeen.current) return undefined;
    tokenSeen.current = token;
    setStatus(null);
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (!token) return undefined;

    // A navigation must not cancel the first check; only signing out or in as somebody else does.
    const stale = () => tokenSeen.current !== token;
    const beat = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        // A hidden tab only counts while something is playing; nothing is added for a tab nobody is looking at.
        const playing = Array.from(document.querySelectorAll("audio, video")).some((m) => !m.paused);
        if (!playing) return;
      }
      try {
        const next = await parentalApi.heartbeat();
        if (stale()) return;
        setStatus(next);
        if (next?.locked) pauseMedia();
        // `tracked` = a linked teen with no limits: nothing is shown, but the time is still counted for the parent's summary
        if (!next?.applies && !next?.tracked && timer.current) { clearInterval(timer.current); timer.current = null; }
      } catch (e) { /* ignore */ }
    };
    (async () => {
      try {
        const first = await parentalApi.usage();
        if (stale() || !(first?.applies || first?.tracked)) return;
        setStatus(first);
        if (first.locked) pauseMedia();
        timer.current = setInterval(beat, BEAT_MS);
        beat();
      } catch (e) { /* ignore */ }
    })();
    return undefined;
  }, [pathname]);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  if (!status?.applies) return null;
  if (status.locked) return <LockScreen status={status} />;
  if (status.remaining_seconds !== null && status.remaining_seconds <= WARN_SECONDS) {
    return (
      <div className="fixed bottom-4 left-1/2 z-[1500] -translate-x-1/2 rounded-full bg-gray-900 px-5 py-2 text-sm font-semibold text-white shadow-lg" role="status" data-testid="usage-warning">
        {minutesLeft(status.remaining_seconds)} {minutesLeft(status.remaining_seconds) === 1 ? "minute" : "minutes"} left today
      </div>
    );
  }
  return null;
}
